/**
 * Pure logic for the template gallery: category filtering and the create
 * payloads that turn a template (or a blank slate) into a draft live-chat
 * campaign.
 *
 * Templates arrive from GET /campaign_templates as
 *   { id, name, category, channel, content, default_trigger_rules, position }
 * where `content` follows the campaign content contract and
 * `default_trigger_rules` may carry any of url / time_on_page /
 * visitor_type / frequency — keys a template omits fall back to the
 * builder defaults.
 *
 * WhatsApp templates are the exception: they carry `content.description` and
 * `content.body` only, because a WhatsApp campaign always sends through a
 * Meta-approved message template picked on the campaign form. Their body is
 * suggested copy, not the message — see `buildWhatsAppStarter`.
 */
import {
  buildContentPayload,
  normalizeCampaignContent,
} from './campaignContent';
import {
  DEFAULT_TIME_ON_PAGE,
  FREQUENCIES,
  VISITOR_TYPES,
} from './campaignBuilder';

export const TEMPLATE_CATEGORY_ALL = 'all';

export const TEMPLATE_CATEGORIES = [
  'welcome',
  'sales',
  'lead_generation',
  'updates',
  'loyalty',
  'self_service',
];

export const TEMPLATE_CHANNELS = {
  LIVE_CHAT: 'live_chat',
  SMS: 'sms',
  WHATSAPP: 'whatsapp',
};

export const filterTemplatesByCategory = (templates, category) =>
  category === TEMPLATE_CATEGORY_ALL
    ? templates
    : templates.filter(template => template.category === category);

/**
 * Templates seeded before the channel column shipped have no `channel`, so an
 * absent one reads as live chat — the same fallback as the column default.
 */
export const filterTemplatesByChannel = (templates, channel) =>
  templates.filter(
    template => (template.channel ?? TEMPLATE_CHANNELS.LIVE_CHAT) === channel
  );

/**
 * What a WhatsApp gallery pick hands the campaign form.
 *
 * A WhatsApp send can only carry a Meta-approved message template, so the
 * gallery cannot write the message itself. It prefills the campaign title and
 * passes the suggested copy through as guidance: the form shows it next to the
 * template picker so the agent can match an approved template or submit this
 * copy as a new one.
 */
export const buildWhatsAppStarter = template => ({
  title: template.name,
  description: template.content?.description ?? '',
  suggestedBody: template.content?.body ?? '',
});

/**
 * What an SMS gallery pick hands the campaign form.
 *
 * The asymmetry with `buildWhatsAppStarter` is deliberate: an SMS send carries
 * the campaign's own message, Liquid-rendered at send time, so the seeded body
 * is the message and prefills the field the user edits. WhatsApp cannot do that
 * — its send must carry a Meta-approved template — so its body stays beside the
 * form as guidance instead.
 */
export const buildSmsStarter = template => ({
  title: template.name,
  message: template.content?.body ?? '',
});

const DEFAULT_TRIGGER_RULES = {
  time_on_page: DEFAULT_TIME_ON_PAGE,
  visitor_type: VISITOR_TYPES.ALL,
  frequency: FREQUENCIES.EVERY_VISIT,
};

/**
 * Template defaults merged over the builder defaults. A blank/absent `url`
 * is dropped entirely — the model validates any present `url` value, so an
 * empty string would fail, while a missing key is a valid "not set yet".
 */
const mergeTriggerRules = defaultTriggerRules => {
  const rules = { ...DEFAULT_TRIGGER_RULES, ...(defaultTriggerRules ?? {}) };
  if (!rules.url) delete rules.url;
  return rules;
};

const draftBase = inboxId => ({
  inbox_id: inboxId,
  enabled: false,
  campaign_status: 'draft',
});

/**
 * The draft created when a template card is picked. `message` mirrors the
 * builder's projection (body, falling back to headline) so legacy widget
 * rendering and card previews stay populated.
 */
export const buildTemplateDraftPayload = ({ template, inboxId }) => {
  const content = normalizeCampaignContent({ content: template.content });

  return {
    title: template.name,
    message: content.body.trim() ? content.body : content.headline,
    content: buildContentPayload(content),
    trigger_rules: mergeTriggerRules(template.default_trigger_rules),
    ...draftBase(inboxId),
  };
};

/**
 * The draft behind "Start from scratch": explicitly emptied content (so the
 * builder does not resurrect legacy fallbacks) with the title standing in
 * for the model-required `message`.
 */
export const buildScratchDraftPayload = ({ title, inboxId }) => ({
  title,
  message: title,
  content: buildContentPayload({
    headline: '',
    body: '',
    imageUrl: '',
    ctas: [],
  }),
  trigger_rules: mergeTriggerRules(),
  ...draftBase(inboxId),
});

/**
 * The channel cards the create dialog offers, in picker order, with only the
 * parts that are pure logic — the page maps `label`, `description`, and
 * `unavailableReason` on afterwards from the i18n catalogue.
 *
 * A channel whose inbox is missing stays in the list but unavailable, so the
 * dialog can show what unlocks it. WhatsApp behind a closed feature flag is a
 * different case: the account does not have the product, so it is omitted
 * entirely rather than advertised as locked.
 */
export const buildCreateChannels = ({
  hasWebsiteInbox,
  hasSmsInbox,
  hasWhatsAppInbox,
  isWhatsAppEnabled,
}) => {
  const channels = [
    {
      value: TEMPLATE_CHANNELS.LIVE_CHAT,
      icon: 'i-lucide-message-square',
      isAvailable: hasWebsiteInbox,
    },
    {
      value: TEMPLATE_CHANNELS.SMS,
      icon: 'i-lucide-message-circle',
      isAvailable: hasSmsInbox,
    },
  ];

  if (isWhatsAppEnabled) {
    channels.push({
      value: TEMPLATE_CHANNELS.WHATSAPP,
      icon: 'i-ri-whatsapp-fill',
      isAvailable: hasWhatsAppInbox,
    });
  }

  return channels;
};
